import axios from 'axios';
import { DateTime } from 'luxon';

export const sanitizeHTML = html => {
  if (!html) return '';

  // Check if the message contains a full HTML document
  const isFullHTML = /<!DOCTYPE html>/i.test(html);

  if (isFullHTML) {
    // Extract body content only
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : 'Invalid Message Format';
  }

  return html.replace(/&nbsp;/g, ' '); // Clean up spaces in normal HTML messages
};

export const formatNotificationTime = timestamp => {
  let date = new Date(timestamp);

  let hours = date.getHours();
  let minutes = date.getMinutes();

  let year = date.getFullYear();
  let month = date.getMonth() + 1; // Month is 0-indexed
  let day = date.getDate();

  // Format date to MM/DD/YYYY
  month = month < 10 ? '0' + month : month;
  day = day < 10 ? '0' + day : day;

  let ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;

  minutes = minutes < 10 ? '0' + minutes : minutes;

  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
};

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
export const formatDateUTCRangeFilter = date => {
  if (!date) return '';
  const localDate = new Date(date);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = utcDate.getUTCFullYear();
  return `${year}-${month}-${day}`;
};
export const formatDateUTC = date => {
  if (!date) return '';
  const localDate = new Date(date);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = monthNames[utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();

  return `${month} ${day}, ${year}`;
};

export const formatDateUTCFormat = date => {
  if (!date) return '';
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  console.log('User Time Zone:', userTimeZone);

  // Convert PickupDate to user's time zone
  const pickupDate = DateTime.fromISO(date, {
    zone: userTimeZone
  }).startOf('day');
  const localDate = new Date(pickupDate);
  const utcDate = new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );
  const day = utcDate.getUTCDate().toString().padStart(2, '0');
  const month = monthNames[utcDate.getUTCMonth()];
  const year = utcDate.getUTCFullYear();

  return `${month} ${day}, ${year}`;
};

export const deformatDateUTC = dDate => {
  const [date, year] = dDate.split(',');
  const month = date.slice(0, 3);
  const day = date.slice(3);

  const montth = (monthNames.indexOf(month) + 1).toString();

  return `${year.trim()}-${montth.padStart(2, 0)}-${day.trim()}`;
};

export function formatPhoneNumber(phoneNumber) {
  const cleaned = ('' + phoneNumber).replace(/\D/g, ''); // Remove non-digit characters
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/); // Match groups
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber; // Return the original number if it doesn't match
}

const GOOGLE_API_KEY = 'AIzaSyBZUlA6YY8998nbFVsE_VuXZ4JpQRJwgHY';

export const getCoordinatesByAddress = async address => {
  if (!address) {
    return [];
  }
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json`,
      {
        params: {
          address: address,
          key: GOOGLE_API_KEY,
          components: `postal_code:${address}|country:US|country:CA|country:MX`
        }
      }
    );

    if (response.data.status === 'OK') {
      const filteredResult = response.data.results.find(result => {
        const addressComponents = result.address_components;

        const postalCode = addressComponents
          .find(component => component.types.includes('postal_code'))
          ?.short_name?.replace(/\s/g, ''); // Remove spaces in postal code

        const city = addressComponents.find(component =>
          component.types.includes('locality')
        )?.short_name;

        const state = addressComponents.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.short_name;

        const country = addressComponents.find(component =>
          component.types.includes('country')
        )?.short_name;

        return city && state && postalCode && country; // Ensure valid match
      });

      if (filteredResult) {
        const postalCode = filteredResult.address_components
          .find(component => component.types.includes('postal_code'))
          ?.short_name?.replace(/\s/g, '');

        const state = filteredResult.address_components.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.short_name;

        let country = filteredResult.address_components.find(component =>
          component.types.includes('country')
        )?.short_name;
        if (country === 'CA') country = 'CAN';
        if (country === 'US') country = 'USA';

        // Extract postcode_localities if available
        const postcodeLocalities = filteredResult.postcode_localities || [];

        let finalAddressArray = [];

        if (postcodeLocalities.length > 0) {
          // Multiple cities exist for the ZIP code
          finalAddressArray = postcodeLocalities.map(
            city => `${city} ${state} ${postalCode} ${country}`
          );
        } else {
          // Single city case
          const city = filteredResult.address_components.find(component =>
            component.types.includes('locality')
          )?.short_name;

          finalAddressArray = [`${city} ${state} ${postalCode} ${country}`];
        }

        return finalAddressArray;
      } else {
        console.log('No valid address found.');
        return [];
      }
    }
  } catch (error) {
    console.error('Geocoding Error:', error);
    return [];
  }
};

export const getAddressByGoogleApis = async address => {
  const response = await axios
    .get('https://maps.googleapis.com/maps/api/place/autocomplete/json', {
      params: {
        input: address, // this is what the user types
        key: GOOGLE_API_KEY,
        types: 'address' // optional, limits results to addresses
        // components: 'country:us' // optional, restrict to specific country
      }
    })
    .then(res => res)
    .catch(err => console.log(err, 'google api err------'));
  console.log(
    response.data.predictions.map(item => item.description),
    'response'
  );
  return response.data.predictions ? response.data.predictions : [];
};

export const getAddressByGoogleMapApis = async address => {
  try {
    // Step 1: Get autocomplete predictions
    const autocompleteResponse = await axios.get(
      'https://maps.googleapis.com/maps/api/place/autocomplete/json',
      {
        params: {
          input: address,
          key: GOOGLE_API_KEY,
          types: 'address'
          // components: 'country:us' // Optional filter for US only
        }
      }
    );

    const predictions = autocompleteResponse.data.predictions;

    if (!predictions || predictions.length === 0) {
      return [];
    }

    // Step 2: Loop over predictions and fetch place details for each
    const detailedResults = await Promise.all(
      predictions.map(async prediction => {
        const placeId = prediction.place_id;
        const fullAddress = prediction.description;

        try {
          const detailsResponse = await axios.get(
            'https://maps.googleapis.com/maps/api/place/details/json',
            {
              params: {
                place_id: placeId,
                key: GOOGLE_API_KEY,
                fields: 'address_component'
              }
            }
          );

          const components = detailsResponse.data.result.address_components;

          const getComponent = type =>
            components.find(c => c.types.includes(type))?.long_name || '';

          return {
            zip: getComponent('postal_code'),
            city: getComponent('locality') || getComponent('sublocality'),
            state: getComponent('administrative_area_level_1'),
            country: getComponent('country'),
            placeId,
            fullAddress
          };
        } catch (detailErr) {
          console.error(`Error fetching details for ${placeId}`, detailErr);
          return null;
        }
      })
    );

    // Filter out any null results in case some detail calls failed
    return detailedResults.filter(item => item !== null);
  } catch (err) {
    console.error('Google API error', err);
    return [];
  }
};

const extractZipFromInput = input => {
  const zipMatch = input.match(/\b\d{5}(?:-\d{4})?\b/);
  return zipMatch ? zipMatch[0] : '';
};

// Enhanced component extractor with ZIP preference
const extractComponent = (components, type, preferredZip = '') => {
  if (type === 'postal_code' && preferredZip) {
    // Look for postal_code component that matches preferred ZIP
    const preferred = components.find(
      c => c.types.includes(type) && c.long_name === preferredZip
    );
    if (preferred) return preferred.long_name;

    // Fallback: check if preferredZip exists *anywhere*
    const anywhere = components.find(c => c.long_name === preferredZip);
    if (anywhere) return preferredZip;
  }

  // Default behavior
  const match = components.find(c => c.types.includes(type));
  return match?.long_name || '';
};

export const getAddressDetailsFromFreeFormAddress = async address => {
  try {
    const userZip = extractZipFromInput(address);

    const geoResponse = await axios.get(
      'https://maps.googleapis.com/maps/api/geocode/json',
      {
        params: {
          address,
          key: GOOGLE_API_KEY
        }
      }
    );

    if (
      geoResponse.data.status !== 'OK' ||
      geoResponse.data.results.length === 0
    ) {
      throw new Error('No results found');
    }

    const result = geoResponse.data.results[0];
    const components = result.address_components;

    const zip = extractComponent(components, 'postal_code', userZip);
    const city =
      extractComponent(components, 'locality') ||
      extractComponent(components, 'sublocality') ||
      extractComponent(components, 'administrative_area_level_2');
    const state = extractComponent(components, 'administrative_area_level_1');
    const country = extractComponent(components, 'country');
    const fullAddress = result.formatted_address;
    const placeId = result.place_id;

    return {
      zip,
      city,
      state,
      country,
      placeId,
      fullAddress
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};
